import type { CurrencyCode } from '../types';

/**
 * Convert 1 unit of `currency` into AUD using the live forex feed.
 *
 * `liveRates` follow the live API convention: AUD is the base and each
 * value is "how many units of CCY per 1 AUD" (e.g. rates.HKD = 5.0 means
 * 1 AUD = 5 HKD). We invert to get AUD-per-unit.
 *
 * Throws `FxRateMissingError` if `liveRates` is absent or doesn't contain
 * the requested currency. The caller (mapper / result page) is responsible
 * for catching this and surfacing a hard error to the user — there is
 * intentionally no static fallback table.
 */
export class FxRateMissingError extends Error {
  readonly currency: string;
  constructor(currency: string) {
    super(`FX rate unavailable for ${currency}`);
    this.name = 'FxRateMissingError';
    this.currency = currency;
  }
}

export function fxToAud(
  currency: CurrencyCode | string,
  liveRates: Record<string, number> | null | undefined,
): number {
  const ccy = String(currency);
  if (ccy === 'AUD') return 1.0;
  if (!liveRates) throw new FxRateMissingError(ccy);
  const apiRate = liveRates[ccy];
  if (typeof apiRate !== 'number' || apiRate <= 0) {
    throw new FxRateMissingError(ccy);
  }
  return 1 / apiRate;
}
